// POS & Business Management Firestore service layer
// Hybrid: localStorage for instant reads + Firestore for persistence
// ALL functions are department-scoped (kitchen | bakery | yawehs)
//
// NOTE (BUGS.md BUG-002): this file still uses the Firebase SDK for
// writes, contrary to the project-wide "REST API only" rule. A
// migration is pending; in the interim the fsSet/fsBatchSet helpers
// below capture failures to Sentry so writes that fail don't vanish
// silently.
import * as Sentry from '@sentry/react'
import { db } from './firebase'
import { collection, doc, setDoc, getDocs, query, where, orderBy, deleteDoc, writeBatch, onSnapshot, limit } from 'firebase/firestore'
import { logAuditEvent } from './accounting'

// ────────────────────────────────────────────
// Generic helpers
// ────────────────────────────────────────────

function lsKey(ns) { return `desres_pos_${ns}` }

function load(ns) {
  try { return JSON.parse(localStorage.getItem(lsKey(ns)) || '[]') }
  catch { return [] }
}

function save(ns, data) {
  localStorage.setItem(lsKey(ns), JSON.stringify(data))
}

function genId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 8) }

async function fsSet(col, record) {
  try { await setDoc(doc(db, col, record.id), record) }
  catch (err) {
    console.warn(`[posStore] fsSet ${col} failed:`, err?.message)
    Sentry.captureException(err, { extra: { module: 'posStore', fn: 'fsSet', collection: col, id: record?.id } })
  }
}

async function fsBatchSet(col, records) {
  try {
    const batch = writeBatch(db)
    for (const r of records) batch.set(doc(db, col, r.id), r)
    await batch.commit()
  } catch (err) {
    console.warn(`[posStore] fsBatchSet ${col} failed:`, err?.message)
    Sentry.captureException(err, { extra: { module: 'posStore', fn: 'fsBatchSet', collection: col, count: records?.length } })
  }
}

// Fire-and-forget delete wrapper — replaces `.catch(() => {})` inline
// so POS deletes that fail on the server don't leave orphaned docs
// unreported.
function fsDelete(col, id) {
  deleteDoc(doc(db, col, id)).catch(err => {
    console.warn(`[posStore] fsDelete ${col}/${id} failed:`, err?.message)
    Sentry.captureException(err, { extra: { module: 'posStore', fn: 'fsDelete', collection: col, id } })
  })
}

// Helper: get branch from department (for Yoco routing)
export function getBranch(departmentId) {
  if (departmentId === 'yawehs') return 'yahwehs_delight'
  return 'desres_kitchen' // kitchen + bakery both under Desres Kitchen
}

// ────────────────────────────────────────────
// POS Products (full catalog for POS terminal)
// ────────────────────────────────────────────

export function getPosProducts(posDept, departmentId) {
  let all = load('products')
  // Filter by business department first
  if (departmentId) all = all.filter(p => p.departmentId === departmentId)
  // Then optionally filter by POS sub-category (e.g. 'sandwich', 'drinks')
  if (posDept) all = all.filter(p => p.department === posDept)
  return all
}

export function getPosProduct(id) {
  return load('products').find(p => p.id === id)
}

export function getPosProductByCode(code, departmentId) {
  const all = load('products')
  return all.find(p => p.code === code && (!departmentId || p.departmentId === departmentId))
}

export function getAllPosProducts(departmentId) {
  const all = load('products')
  return departmentId ? all.filter(p => p.departmentId === departmentId) : all
}

export function savePosProduct(product) {
  const all = load('products')
  const existing = all.findIndex(p => p.id === product.id)
  const record = { ...product, updatedAt: new Date().toISOString() }
  if (!record.id) record.id = genId()
  if (!record.createdAt) record.createdAt = new Date().toISOString()
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save('products', all)
  fsSet('posProducts', record)
  logAuditEvent({ action: existing >= 0 ? 'UPDATE' : 'CREATE', entity: 'product', entityId: record.id, departmentId: record.departmentId, details: record.name })
  return record
}

export function deletePosProduct(id) {
  save('products', load('products').filter(p => p.id !== id))
  fsDelete('posProducts', id)
  logAuditEvent({ action: 'DELETE', entity: 'product', entityId: id })
}

// ────────────────────────────────────────────
// Recipes
// ────────────────────────────────────────────

export function getRecipes(departmentId) {
  const all = load('recipes')
  return departmentId ? all.filter(r => r.departmentId === departmentId) : all
}

export function getRecipe(id) { return load('recipes').find(r => r.id === id) }

export function getRecipeByProductId(productId) {
  return load('recipes').find(r => r.productId === productId)
}

export function saveRecipe(recipe) {
  const all = load('recipes')
  const existing = all.findIndex(r => r.id === recipe.id)
  const record = { ...recipe, updatedAt: new Date().toISOString() }
  if (!record.id) record.id = genId()
  if (!record.createdAt) record.createdAt = new Date().toISOString()
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save('recipes', all)
  fsSet('posRecipes', record)
  logAuditEvent({ action: existing >= 0 ? 'UPDATE' : 'CREATE', entity: 'recipe', entityId: record.id, departmentId: record.departmentId })
  return record
}

export function deleteRecipe(id) {
  save('recipes', load('recipes').filter(r => r.id !== id))
  fsDelete('posRecipes', id)
  logAuditEvent({ action: 'DELETE', entity: 'recipe', entityId: id })
}

// ────────────────────────────────────────────
// Inventory Items (ingredients / raw materials)
// ────────────────────────────────────────────

export function getInventoryItems(departmentId, location) {
  let all = load('inventory')
  if (departmentId) all = all.filter(i => i.departmentId === departmentId)
  if (location) all = all.filter(i => i.location === location)
  return all
}

export function getInventoryItem(id) { return load('inventory').find(i => i.id === id) }

export function saveInventoryItem(item) {
  const all = load('inventory')
  const existing = all.findIndex(i => i.id === item.id)
  const record = { ...item, updatedAt: new Date().toISOString() }
  if (!record.id) record.id = genId()
  if (!record.createdAt) record.createdAt = new Date().toISOString()
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save('inventory', all)
  fsSet('posInventory', record)
  logAuditEvent({ action: existing >= 0 ? 'UPDATE' : 'CREATE', entity: 'inventory', entityId: record.id, departmentId: record.departmentId, details: record.name })
  return record
}

export function adjustStock(itemId, qtyChange, reason, date) {
  const items = load('inventory')
  const idx = items.findIndex(i => i.id === itemId)
  if (idx < 0) return null
  items[idx].currentStock = (items[idx].currentStock || 0) + qtyChange
  items[idx].updatedAt = new Date().toISOString()
  save('inventory', items)
  fsSet('posInventory', items[idx])

  // Log movement
  const movement = {
    id: genId(), itemId, qtyChange, reason, date: date || new Date().toISOString().split('T')[0],
    departmentId: items[idx].departmentId,
    balanceAfter: items[idx].currentStock, createdAt: new Date().toISOString()
  }
  const movements = load('stockMovements')
  movements.push(movement)
  save('stockMovements', movements)
  fsSet('posStockMovements', movement)
  return items[idx]
}

export function getStockMovements(departmentId, itemId, dateFrom, dateTo) {
  let all = load('stockMovements')
  if (departmentId) all = all.filter(m => m.departmentId === departmentId)
  if (itemId) all = all.filter(m => m.itemId === itemId)
  if (dateFrom) all = all.filter(m => m.date >= dateFrom)
  if (dateTo) all = all.filter(m => m.date <= dateTo)
  return all.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

// ────────────────────────────────────────────
// Suppliers
// ────────────────────────────────────────────

export function getSuppliers(departmentId, category) {
  let all = load('suppliers')
  if (departmentId) all = all.filter(s => s.departmentId === departmentId)
  if (category) all = all.filter(s => s.category === category)
  return all
}

export function getSupplier(id) { return load('suppliers').find(s => s.id === id) }

export function saveSupplier(supplier) {
  const all = load('suppliers')
  const existing = all.findIndex(s => s.id === supplier.id)
  const record = { ...supplier, updatedAt: new Date().toISOString() }
  if (!record.id) record.id = genId()
  if (!record.createdAt) record.createdAt = new Date().toISOString()
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save('suppliers', all)
  fsSet('posSuppliers', record)
  logAuditEvent({ action: existing >= 0 ? 'UPDATE' : 'CREATE', entity: 'supplier', entityId: record.id, departmentId: record.departmentId, details: record.name })
  return record
}

export function deleteSupplier(id) {
  save('suppliers', load('suppliers').filter(s => s.id !== id))
  logAuditEvent({ action: 'DELETE', entity: 'supplier', entityId: id })
}

// Supplier price lists
export function getSupplierPrices(supplierId) {
  return load('supplierPrices').filter(p => p.supplierId === supplierId)
}

export function saveSupplierPrice(priceEntry) {
  const all = load('supplierPrices')
  const existing = all.findIndex(p => p.id === priceEntry.id)
  const record = { ...priceEntry, updatedAt: new Date().toISOString() }
  if (!record.id) record.id = genId()
  if (!record.createdAt) record.createdAt = new Date().toISOString()
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save('supplierPrices', all)
  fsSet('posSupplierPrices', record)
  logAuditEvent({ action: 'CREATE', entity: 'supplierPrice', entityId: record.id })
  return record
}

// ────────────────────────────────────────────
// Purchases
// ────────────────────────────────────────────

export function getPurchases(departmentId, supplierId, dateFrom, dateTo) {
  let all = load('purchases')
  if (departmentId) all = all.filter(p => p.departmentId === departmentId)
  if (supplierId) all = all.filter(p => p.supplierId === supplierId)
  if (dateFrom) all = all.filter(p => p.date >= dateFrom)
  if (dateTo) all = all.filter(p => p.date <= dateTo)
  return all.sort((a, b) => b.date.localeCompare(a.date))
}

export function getPurchase(id) { return load('purchases').find(p => p.id === id) }

export function savePurchase(purchase) {
  const all = load('purchases')
  const existing = all.findIndex(p => p.id === purchase.id)
  const record = { ...purchase, updatedAt: new Date().toISOString() }
  if (!record.id) record.id = genId()
  if (!record.createdAt) record.createdAt = new Date().toISOString()
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save('purchases', all)
  fsSet('posPurchases', record)

  // Auto-update inventory stock levels if confirmed
  if (record.status === 'confirmed' && record.lineItems) {
    for (const item of record.lineItems) {
      if (item.inventoryItemId) {
        adjustStock(item.inventoryItemId, Number(item.qty) || 0, `Purchase #${record.id}`, record.date)
        // Update last purchase price
        const inv = getInventoryItem(item.inventoryItemId)
        if (inv) {
          inv.lastPurchasePrice = Number(item.unitPrice) || 0
          saveInventoryItem(inv)
        }
      }
    }
  }
  logAuditEvent({ action: existing >= 0 ? 'UPDATE' : 'CREATE', entity: 'purchase', entityId: record.id, departmentId: record.departmentId })
  return record
}

export function deletePurchase(id) {
  save('purchases', load('purchases').filter(p => p.id !== id))
  logAuditEvent({ action: 'DELETE', entity: 'purchase', entityId: id })
}

// ────────────────────────────────────────────
// POS Sales (transactions)
// ────────────────────────────────────────────

export function getPosSales(departmentId, dateFrom, dateTo) {
  let all = load('posSales')
  if (departmentId) all = all.filter(s => s.departmentId === departmentId)
  if (dateFrom) all = all.filter(s => s.date >= dateFrom)
  if (dateTo) all = all.filter(s => s.date <= dateTo)
  return all.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

export function savePosSale(sale) {
  const all = load('posSales')
  const record = {
    ...sale,
    id: sale.id || genId(),
    createdAt: sale.createdAt || new Date().toISOString()
  }
  all.push(record)
  save('posSales', all)
  fsSet('posSalesRecords', record)

  // Deduct stock from inventory based on recipes
  if (record.items) {
    for (const item of record.items) {
      const recipe = getRecipeByProductId(item.productId)
      if (recipe && recipe.ingredients) {
        for (const ing of recipe.ingredients) {
          if (ing.inventoryItemId) {
            const deduction = -(Number(ing.qty) || 0) * (Number(item.qty) || 1)
            adjustStock(ing.inventoryItemId, deduction, `Sale #${record.id}`, record.date)
          }
        }
      }
    }
  }
  logAuditEvent({ action: 'CREATE', entity: 'sale', entityId: record.id, departmentId: record.departmentId, details: `R${record.total}` })
  return record
}

export function voidPosSale(saleId, reason) {
  const all = load('posSales')
  const idx = all.findIndex(s => s.id === saleId)
  if (idx < 0) return null
  all[idx].voided = true
  all[idx].voidReason = reason
  all[idx].voidedAt = new Date().toISOString()
  save('posSales', all)
  fsSet('posSalesRecords', all[idx])
  logAuditEvent({ action: 'VOID', entity: 'sale', entityId: saleId, details: reason })
  return all[idx]
}

// ────────────────────────────────────────────
// Overheads
// ────────────────────────────────────────────

export function getOverheads(departmentId, month) {
  let all = load('overheads')
  if (departmentId) all = all.filter(o => o.departmentId === departmentId)
  if (month) all = all.filter(o => o.month === month)
  return all
}

export function saveOverhead(overhead) {
  const all = load('overheads')
  const existing = all.findIndex(o => o.id === overhead.id)
  const record = { ...overhead, updatedAt: new Date().toISOString() }
  if (!record.id) record.id = genId()
  if (!record.createdAt) record.createdAt = new Date().toISOString()
  if (existing >= 0) all[existing] = record
  else all.push(record)
  save('overheads', all)
  fsSet('posOverheads', record)
  logAuditEvent({ action: existing >= 0 ? 'UPDATE' : 'CREATE', entity: 'overhead', entityId: record.id, departmentId: record.departmentId, details: record.name })
  return record
}

export function getOverheadAllocations(departmentId) {
  try {
    const key = departmentId ? lsKey(`overheadAllocations_${departmentId}`) : lsKey('overheadAllocations')
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : {
      kitchen: 40, bakery: 35, sandwiches: 10, drinks: 10, other: 5
    }
  } catch {
    return { kitchen: 40, bakery: 35, sandwiches: 10, drinks: 10, other: 5 }
  }
}

export function saveOverheadAllocations(allocations, departmentId) {
  const key = departmentId ? lsKey(`overheadAllocations_${departmentId}`) : lsKey('overheadAllocations')
  localStorage.setItem(key, JSON.stringify(allocations))
  logAuditEvent({ action: 'UPDATE', entity: 'overheadAllocations', departmentId })
}

// ────────────────────────────────────────────
// Wastage
// ────────────────────────────────────────────

export function getWastageLog(departmentId, dateFrom, dateTo) {
  let all = load('wastage')
  if (departmentId) all = all.filter(w => w.departmentId === departmentId)
  if (dateFrom) all = all.filter(w => w.date >= dateFrom)
  if (dateTo) all = all.filter(w => w.date <= dateTo)
  return all.sort((a, b) => b.date.localeCompare(a.date))
}

export function saveWastageEntry(entry) {
  const all = load('wastage')
  const record = { ...entry, id: entry.id || genId(), createdAt: new Date().toISOString() }
  all.push(record)
  save('wastage', all)
  fsSet('posWastage', record)
  // Deduct from inventory
  if (record.inventoryItemId && record.qty) {
    adjustStock(record.inventoryItemId, -Number(record.qty), `Wastage: ${record.reason || 'unspecified'}`, record.date)
  }
  logAuditEvent({ action: 'CREATE', entity: 'wastage', entityId: record.id, departmentId: record.departmentId })
  return record
}

// ────────────────────────────────────────────
// Stocktakes
// ────────────────────────────────────────────

export function getStocktakes(departmentId) {
  let all = load('stocktakes')
  if (departmentId) all = all.filter(s => s.departmentId === departmentId)
  return all.sort((a, b) => b.date.localeCompare(a.date))
}

export function saveStocktake(stocktake) {
  const all = load('stocktakes')
  const record = { ...stocktake, id: stocktake.id || genId(), createdAt: new Date().toISOString() }
  all.push(record)
  save('stocktakes', all)
  fsSet('posStocktakes', record)
  logAuditEvent({ action: 'CREATE', entity: 'stocktake', entityId: record.id, departmentId: record.departmentId })
  return record
}

// ────────────────────────────────────────────
// POS Flags (business intelligence flags)
// ────────────────────────────────────────────

export function getPosFlags(departmentId, resolved) {
  let all = load('posFlags')
  if (departmentId) all = all.filter(f => f.departmentId === departmentId)
  if (resolved !== undefined) all = all.filter(f => f.resolved === resolved)
  return all.sort((a, b) => {
    const sevOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, WARNING: 3 }
    return (sevOrder[a.severity] || 4) - (sevOrder[b.severity] || 4)
  })
}

export function savePosFlag(flag) {
  const all = load('posFlags')
  // Don't duplicate same flag code + product within same day
  const exists = all.find(f =>
    f.flagCode === flag.flagCode &&
    f.productId === flag.productId &&
    f.date === flag.date &&
    f.departmentId === flag.departmentId &&
    !f.resolved
  )
  if (exists) return exists

  const record = { ...flag, id: flag.id || genId(), resolved: false, createdAt: new Date().toISOString() }
  all.push(record)
  save('posFlags', all)
  fsSet('posFlagsRecords', record)
  logAuditEvent({ action: 'CREATE', entity: 'flag', entityId: record.id, departmentId: record.departmentId })
  return record
}

export function resolvePosFlag(id, notes) {
  const all = load('posFlags')
  const idx = all.findIndex(f => f.id === id)
  if (idx < 0) return null
  all[idx].resolved = true
  all[idx].resolvedNotes = notes
  all[idx].resolvedAt = new Date().toISOString()
  save('posFlags', all)
  fsSet('posFlagsRecords', all[idx])
  logAuditEvent({ action: 'RESOLVE', entity: 'flag', entityId: id })
  return all[idx]
}

// ────────────────────────────────────────────
// Costing calculations
// ────────────────────────────────────────────

export function calculateRecipeCost(recipe) {
  if (!recipe || !recipe.ingredients) return 0
  let total = 0
  for (const ing of recipe.ingredients) {
    const invItem = getInventoryItem(ing.inventoryItemId)
    const unitCost = invItem?.lastPurchasePrice || invItem?.avgCost || 0
    // Convert units if needed
    let costPerRecipeUnit = unitCost
    if (ing.unit !== invItem?.unit) {
      // Handle kg→g, L→ml conversions
      if (invItem?.unit === 'kg' && ing.unit === 'g') costPerRecipeUnit = unitCost / 1000
      else if (invItem?.unit === 'L' && ing.unit === 'ml') costPerRecipeUnit = unitCost / 1000
    }
    total += costPerRecipeUnit * (Number(ing.qty) || 0)
  }
  // Divide by batch yield if batch recipe
  if (recipe.batchYield && recipe.batchYield > 1) {
    total = total / recipe.batchYield
  }
  return total
}

export function getProductCostCard(productId) {
  const product = getPosProduct(productId)
  if (!product) return null
  const recipe = getRecipeByProductId(productId)
  const cogs = recipe ? calculateRecipeCost(recipe) : (product.cost || 0)
  const sellingPrice = product.sellingPrice || 0
  const grossProfit = sellingPrice - cogs
  const grossMargin = sellingPrice > 0 ? (grossProfit / sellingPrice) * 100 : 0

  // Get overhead allocation
  const allocations = getOverheadAllocations(product.departmentId)
  const deptAlloc = allocations[product.department] || 10
  // Simplified daily overhead
  const monthOverheads = getOverheads(product.departmentId)
  const totalMonthly = monthOverheads.reduce((s, o) => s + (Number(o.amount) || 0), 0)
  const dailyOverhead = totalMonthly / 26 // ~26 trading days
  const deptDailyOverhead = dailyOverhead * (deptAlloc / 100)
  // Per-product overhead allocation (simplified)
  const overheadPerUnit = 0

  return {
    product,
    recipe,
    cogs,
    sellingPrice,
    grossProfit,
    grossMargin,
    overheadPerUnit,
    netProfit: grossProfit - overheadPerUnit,
    netMargin: sellingPrice > 0 ? ((grossProfit - overheadPerUnit) / sellingPrice) * 100 : 0,
    status: grossMargin < 0 ? 'loss' : grossMargin < 20 ? 'warning' : 'healthy',
  }
}

// ────────────────────────────────────────────
// Sync from Firestore on app load
// ────────────────────────────────────────────

export async function syncPosFromFirestore() {
  const collections = [
    { ns: 'products', col: 'posProducts' },
    { ns: 'recipes', col: 'posRecipes' },
    { ns: 'inventory', col: 'posInventory' },
    { ns: 'suppliers', col: 'posSuppliers' },
    { ns: 'purchases', col: 'posPurchases' },
    { ns: 'posSales', col: 'posSalesRecords' },
    { ns: 'overheads', col: 'posOverheads' },
    { ns: 'posFlags', col: 'posFlagsRecords' },
  ]

  for (const { ns, col } of collections) {
    try {
      const snapshot = await getDocs(collection(db, col))
      if (!snapshot.empty) {
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        save(ns, data)
      }
    } catch (err) {
      console.warn(`[posStore] syncPosFromFirestore ${col} failed:`, err?.message)
      Sentry.captureException(err, { extra: { module: 'posStore', fn: 'syncPosFromFirestore', collection: col } })
    }
  }
}
